// Date helpers. The parsing/formatting lives in @releve/shared/format/date so
// the API can format documents with the same rules; this module keeps the
// browser-side Sentry wiring.

import { format, formatDistanceToNow } from 'date-fns';
import { dbCalendarDayInZone, getDateFnsLocale } from '@releve/shared/format/date';
import { captureMessage } from '@/lib/sentry';

export {
  parseLocalISODate,
  formatLocalISODate,
  safeFormatDate,
  getDateFnsLocale,
  // Venue wall-clock helpers (project event planner): pure string/integer
  // arithmetic on 'HH:mm' / 'YYYY-MM-DDTHH:mm' — never a Date, never a zone.
  parseLocalStamp,
  toLocalStamp,
  addDaysYmd,
  formatLocalTime,
  parseLocalTime,
  diffLocalStampMinutes,
  addLocalStampMinutes,
} from '@releve/shared/format/date';

/**
 * Resolves a DB date value to its calendar day **in the organization's
 * timezone**, returned as a local-midnight `Date` for calendar-day math
 * (`differenceInCalendarDays`, same-day comparisons).
 *
 * `date` columns (`'YYYY-MM-DD'`) pass through untouched — they carry no
 * instant. `timestamptz` columns (`ledger_transactions.transaction_date`)
 * are real instants, so the day only exists relative to a zone: a journal
 * entry picked as "Mar 15, 00:30" in Amsterdam is stored as
 * `2026-03-14T23:30Z`, and resolving it against UTC calls it Mar 14 — which
 * is exactly why bank lines booked Mar 15 refused to match it.
 *
 * Pass `selectedOrganization?.timezone` (nullish is fine — it falls back to
 * the browser's own day resolution, the behavior every caller had before).
 */
export function dbCalendarDayInOrgZone(
  value: string | Date | null | undefined,
  timeZone: string | null | undefined,
): Date | undefined {
  return dbCalendarDayInZone(value, timeZone, () => {
    // Non-empty input that isn't a parseable date. Surfaces silent
    // date-filter regressions in the same class as the timestamptz bug this
    // helper was created to fix — callers commonly use a non-null assertion
    // (`!`) downstream, so an unhandled `undefined` either poisons a sort or
    // silently drops a row. Sentry sees it so we can diagnose without
    // reproducing.
    captureMessage('dbCalendarDayInOrgZone: unparseable DB date', 'warning');
  });
}

/**
 * DISPLAY twin of {@link dbCalendarDayInOrgZone}: renders a DB date value as
 * the calendar day it falls on **in the organization's timezone**.
 *
 * This is `safeFormatDate` with the instant→day resolution moved off the
 * browser and onto the org. Use it wherever a grid or panel prints the day of
 * a `timestamptz` column (`ledger_transactions.transaction_date`,
 * `accounting_imports.processed_at`) — otherwise a bookkeeper in New York and
 * a bookkeeper in Amsterdam read different days off the same row, and the
 * displayed day can disagree with the org-timezone match hints computed by
 * `dbCalendarDayInOrgZone`.
 *
 * Output is byte-identical to `safeFormatDate(value, pattern, fallback)`
 * whenever the browser and the org resolve the same day — including the
 * null/unparseable cases, which return `fallback` either way. `date` columns
 * (`'YYYY-MM-DD'`) carry no instant, so they render identically in every zone.
 *
 * Day-granularity only: the returned day is anchored at midnight, so a pattern
 * carrying a time (`'PPp'`) would print `00:00`. Use `formatInstantInZone` from
 * `@releve/shared/format/date` when the time-of-day itself must render.
 *
 * @param timeZone `selectedOrganization?.timezone`. Nullish falls back to the
 *   browser's own day resolution — the behavior every caller had before this
 *   helper existed — so orgs with no timezone configured see no change.
 */
export function formatOrgDay(
  value: string | Date | null | undefined,
  timeZone: string | null | undefined,
  pattern: string = 'yyyy-MM-dd',
  fallback: string = '—',
): string {
  const day = dbCalendarDayInOrgZone(value, timeZone);
  if (!day) return fallback;
  return format(day, pattern);
}

/**
 * A genuine instant (`timestamptz` — `created_at`, `last_sign_in_at`, an
 * invitation's `expires_at`) as relative time in the UI language: "3 days
 * ago", "in 5 days". Returns `''` for null, a non-string or an unparseable
 * value instead of throwing, so a grid cell never crashes on bad data.
 *
 * For instants only — a business date (`date` column) is a calendar day and
 * goes through `safeFormatDate` (docs/DATES.md).
 */
export function formatRelativeInstant(value: unknown, language: string | null | undefined): string {
  if (typeof value !== 'string' || !value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return formatDistanceToNow(date, { addSuffix: true, locale: getDateFnsLocale(language ?? 'en') });
}
